/**
 * @fileoverview Dammen Game Session Screen
 * @description Active gameplay screen for Dammen
 */

import React, { useState, useEffect, useCallback } from "react";
import { View, Text, StyleSheet, Alert, BackHandler, TouchableOpacity, ActivityIndicator } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useLocalSearchParams, useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { Image } from "expo-image";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import {
  useDammen,
  DammenBoard,
  DammenHeader,
  DammenResultOverlay,
  DammenResult,
  DammenGameConfig,
  DammenWeekendConfig,
  DEFAULT_DAMMEN_GAME_CONFIG,
  DEFAULT_DAMMEN_WEEKEND_CONFIG,
  AIDifficulty,
} from "@/modules/dammen";
import { logActivity } from "@/lib/activity-log";
import { notifyTeamOfPoints, notifyAllUsersOfScore } from "@/modules/games";

type GameMode = "free" | "weekend";

/**
 * Dammen Game Session Screen
 */
export default function DammenGameScreen() {
  const { sessionId, mode: modeParam } = useLocalSearchParams<{
    sessionId: string;
    mode: string;
  }>();
  const router = useRouter();
  const colors = useThemeColors();
  const { activeProfile, userTeam, activeWeekend } = useAuth();

  // State
  const [mode, setMode] = useState<GameMode>((modeParam as GameMode) || "free");
  const [gameConfig, setGameConfig] = useState<DammenGameConfig>(DEFAULT_DAMMEN_GAME_CONFIG);
  const [weekendConfig, setWeekendConfig] = useState<DammenWeekendConfig>(DEFAULT_DAMMEN_WEEKEND_CONFIG);
  const [result, setResult] = useState<DammenResult | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [isAIGame, setIsAIGame] = useState(false);
  const [aiDifficulty, setAiDifficulty] = useState<AIDifficulty>("medium");
  const [lottieKey, setLottieKey] = useState<string | undefined>();
  const [isWaitingRoom, setIsWaitingRoom] = useState(false);
  const [waitingRoomData, setWaitingRoomData] = useState<{
    challengerName: string;
    challengedName: string;
    isChallenger: boolean;
    challengerReady: boolean;
    challengedReady: boolean;
  } | null>(null);

  /**
   * Character SVG images
   */
  const CHARACTER_IMAGES = {
    opa: require("@/assets/games/opa_endless_runner.svg"),
    oma: require("@/assets/games/omam_endless_runner.svg"),
  };

  /**
   * Handle game over
   */
  const handleGameOver = useCallback(
    async (gameResult: DammenResult) => {
      if (!activeProfile?.id || !sessionId) return;

      const isWin = gameResult.winnerPiece !== null && gameResult.winnerId === activeProfile.id;
      const isDraw = gameResult.isDraw;

      // Calculate points for weekend mode
      let points = 0;
      if (mode === "weekend") {
        if (isWin) {
          points = weekendConfig.points_win;
        } else if (isDraw) {
          points = weekendConfig.points_draw;
        } else {
          points = weekendConfig.points_participation;
        }
        setPointsEarned(points);
      }

      // Update session with result
      await supabase
        .from("game_sessions")
        .update({
          status: "completed",
          completed_at: new Date().toISOString(),
          winner_user_id: gameResult.winnerId,
          points_awarded: points,
          result: gameResult,
        })
        .eq("id", sessionId);

      // Update user stats
      const { data: existingStats } = await supabase
        .from("user_game_stats")
        .select("games_played, games_won, games_lost, games_drawn, stats")
        .eq("user_id", activeProfile.id)
        .eq("game_slug", "dammen")
        .eq("mode", mode)
        .single();

      const currentStats = existingStats?.stats || {};
      const newStats = {
        games_played: (existingStats?.games_played || 0) + 1,
        games_won: (existingStats?.games_won || 0) + (isWin ? 1 : 0),
        games_lost: (existingStats?.games_lost || 0) + (!isWin && !isDraw ? 1 : 0),
        games_drawn: (existingStats?.games_drawn || 0) + (isDraw ? 1 : 0),
        stats: {
          ...currentStats,
          current_streak: isWin ? (currentStats.current_streak || 0) + 1 : 0,
          longest_streak: isWin
            ? Math.max((currentStats.longest_streak || 0), (currentStats.current_streak || 0) + 1)
            : currentStats.longest_streak || 0,
        },
      };

      await supabase.from("user_game_stats").upsert(
        {
          user_id: activeProfile.id,
          game_slug: "dammen",
          mode,
          ...newStats,
          last_played_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        {
          onConflict: "user_id,game_slug,mode",
        }
      );

      // Weekend mode: update team score and send notifications
      if (mode === "weekend" && points > 0 && userTeam?.id) {
        // Update team score
        const { data: teamData } = await supabase
          .from("teams")
          .select("total_score")
          .eq("id", userTeam.id)
          .single();

        const previousTotal = teamData?.total_score || 0;
        const newTotal = previousTotal + points;

        await supabase
          .from("teams")
          .update({ total_score: newTotal })
          .eq("id", userTeam.id);

        // Record in score_history
        await supabase.from("score_history").insert({
          team_id: userTeam.id,
          points,
          previous_total: previousTotal,
          new_total: newTotal,
          reason: isWin
            ? `🏆 Dammen gewonnen!`
            : isDraw
            ? `🤝 Dammen gelijkspel`
            : `Dammen gespeeld`,
          game_type: "dammen",
          game_id: sessionId,
          awarded_to: activeProfile.id,
        });

        // Send notifications
        await notifyTeamOfPoints({
          earnerId: activeProfile.id,
          earnerName: activeProfile.full_name || "Iemand",
          teamId: userTeam.id,
          points,
          gameName: "Dammen",
          reason: isWin ? "Gewonnen" : isDraw ? "Gelijkspel" : "Gespeeld",
          weekendId: activeWeekend?.id,
          sessionId,
        });

        // Silent notification to all users
        if (activeWeekend?.id) {
          await notifyAllUsersOfScore({
            earnerId: activeProfile.id,
            earnerName: activeProfile.full_name || "Iemand",
            teamName: userTeam.name || "Team",
            points,
            gameName: "Dammen",
            weekendId: activeWeekend.id,
            isNewRecord: false,
          });
        }

        // Log activity
        await logActivity({
          user_id: activeProfile.id,
          action_type: "score.award",
          action_category: "score",
          target_type: "game_session",
          target_id: sessionId,
          target_name: "Dammen",
          metadata: {
            game_slug: "dammen",
            points,
            result: isWin ? "win" : isDraw ? "draw" : "loss",
            team_id: userTeam.id,
          },
          weekend_id: activeWeekend?.id,
        });
      }

      // Set lottie based on result
      if (isWin && gameConfig.win_lottie) {
        setLottieKey(gameConfig.win_lottie);
      } else if (!isWin && !isDraw && gameConfig.lose_lottie) {
        setLottieKey(gameConfig.lose_lottie);
      } else if (isDraw && gameConfig.draw_lottie) {
        setLottieKey(gameConfig.draw_lottie);
      }

      setResult(gameResult);
      setShowResult(true);
    },
    [activeProfile, sessionId, mode, weekendConfig, userTeam, activeWeekend, gameConfig]
  );

  // Game hook
  const {
    board,
    currentTurn,
    myPiece,
    isMyTurn,
    gameStatus,
    winner,
    selectedPosition,
    validMoves,
    mustJumpFrom,
    capturedPieces,
    opponentInfo,
    moveCount,
    isLoading,
    error,
    selectCell,
    forfeit,
    reloadGameState,
  } = useDammen({
    sessionId: sessionId || "",
    mode: isAIGame ? "solo" : "versus",
    aiDifficulty,
    onGameEnd: handleGameOver,
  });

  /**
   * Load session data on mount and subscribe to updates
   */
  useEffect(() => {
    if (!sessionId || !activeProfile?.id) return;

    const loadSession = async () => {
      const { data: session } = await supabase
        .from("game_sessions")
        .select("game_state, mode, weekend_id, status")
        .eq("id", sessionId)
        .single();

      if (session) {
        setMode(session.mode as GameMode);
        
        const gameState = session.game_state as any;
        if (gameState?.isAIGame) {
          setIsAIGame(true);
          setAiDifficulty(gameState.aiDifficulty || "medium");
        }

        // Check if this is a waiting room scenario (versus game waiting for opponent)
        if (session.status === "waiting" && !gameState?.isAIGame) {
          setIsWaitingRoom(true);
          const isChallenger = gameState?.challenger_id === activeProfile.id;
          setWaitingRoomData({
            challengerName: gameState?.challenger_name || "Uitdager",
            challengedName: gameState?.challenged_user_name || "Tegenstander",
            isChallenger,
            challengerReady: gameState?.challenger_ready || false,
            challengedReady: gameState?.challenged_ready || false,
          });
        } else if (session.status === "active") {
          setIsWaitingRoom(false);
        }
      }

      // Fetch game config
      const { data: gameDef } = await supabase
        .from("game_definitions")
        .select("default_config")
        .eq("slug", "dammen")
        .single();

      if (gameDef?.default_config) {
        setGameConfig({
          ...DEFAULT_DAMMEN_GAME_CONFIG,
          ...gameDef.default_config,
        });
      }

      // Fetch weekend config if applicable
      if (session?.weekend_id) {
        const { data: weekendGame } = await supabase
          .from("weekend_game_config")
          .select("points_config, custom_config")
          .eq("weekend_id", session.weekend_id)
          .eq("game_slug", "dammen")
          .single();

        if (weekendGame) {
          setWeekendConfig({
            ...DEFAULT_DAMMEN_WEEKEND_CONFIG,
            ...(weekendGame.points_config as any),
            ...(weekendGame.custom_config as any),
          });
        }
      }
    };

    loadSession();

    // Subscribe to session updates for real-time waiting room status
    const channel = supabase
      .channel(`dammen-session:${sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "game_sessions",
          filter: `id=eq.${sessionId}`,
        },
        (payload) => {
          const newData = payload.new as any;
          const gameState = newData.game_state;

          if (newData.status === "active") {
            setIsWaitingRoom(false);
          } else if (newData.status === "waiting") {
            setWaitingRoomData({
              challengerName: gameState?.challenger_name || "Uitdager",
              challengedName: gameState?.challenged_user_name || "Tegenstander",
              isChallenger: gameState?.challenger_id === activeProfile.id,
              challengerReady: gameState?.challenger_ready || false,
              challengedReady: gameState?.challenged_ready || false,
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId, activeProfile?.id]);

  /**
   * Handle back button
   */
  useEffect(() => {
    const handleBackPress = () => {
      if (gameStatus === "playing" && !showResult) {
        Alert.alert(
          "Spel verlaten?",
          "Als je nu weggaat verlies je dit spel.",
          [
            { text: "Annuleren", style: "cancel" },
            {
              text: "Opgeven",
              style: "destructive",
              onPress: () => {
                forfeit();
              },
            },
          ]
        );
        return true;
      }
      return false;
    };

    const subscription = BackHandler.addEventListener(
      "hardwareBackPress",
      handleBackPress
    );
    return () => subscription.remove();
  }, [gameStatus, showResult, forfeit]);

  /**
   * Reload game state when waiting room closes (opponent triggers start)
   */
  const prevIsWaitingRoom = React.useRef(isWaitingRoom);
  useEffect(() => {
    if (prevIsWaitingRoom.current && !isWaitingRoom) {
      // Waiting room just closed - reload game state
      reloadGameState();
    }
    prevIsWaitingRoom.current = isWaitingRoom;
  }, [isWaitingRoom, reloadGameState]);

  /**
   * Handle ready in waiting room
   */
  const handleReady = async () => {
    if (!sessionId || !activeProfile?.id) return;

    try {
      const { data: session } = await supabase
        .from("game_sessions")
        .select("game_state")
        .eq("id", sessionId)
        .single();

      const gameState = session?.game_state as any;
      const isChallenger = gameState?.challenger_id === activeProfile.id;
      
      const updatedGameState = {
        ...gameState,
        challenger_ready: isChallenger ? true : gameState?.challenger_ready,
        challenged_ready: !isChallenger ? true : gameState?.challenged_ready,
      };

      // Check if both players are now ready
      const bothReady = updatedGameState.challenger_ready && updatedGameState.challenged_ready;

      await supabase
        .from("game_sessions")
        .update({
          game_state: {
            ...updatedGameState,
            status: bothReady ? "playing" : "waiting",
          },
          status: bothReady ? "active" : "waiting",
          started_at: bothReady ? new Date().toISOString() : null,
        })
        .eq("id", sessionId);

      if (bothReady) {
        setIsWaitingRoom(false);
        // Reload game state to ensure myPiece and gameStatus are set
        await reloadGameState();
      }
    } catch (error) {
      console.error("Error setting ready:", error);
    }
  };

  /**
   * Handle cancel challenge
   */
  const handleCancelChallenge = async () => {
    Alert.alert(
      "Uitdaging annuleren?",
      "Weet je zeker dat je de uitdaging wilt annuleren?",
      [
        { text: "Nee", style: "cancel" },
        {
          text: "Ja, annuleren",
          style: "destructive",
          onPress: async () => {
            try {
              await supabase
                .from("game_sessions")
                .update({ status: "cancelled" })
                .eq("id", sessionId);
              router.replace("/(tabs)/games/dammen");
            } catch (error) {
              console.error("Error cancelling challenge:", error);
            }
          },
        },
      ]
    );
  };

  /**
   * Handle play again
   */
  const handlePlayAgain = () => {
    router.replace("/(tabs)/games/dammen");
  };

  /**
   * Handle back
   */
  const handleBack = () => {
    router.replace("/(tabs)/games/dammen");
  };

  // Show waiting room if applicable
  if (isWaitingRoom && waitingRoomData) {
    const myReady = waitingRoomData.isChallenger 
      ? waitingRoomData.challengerReady 
      : waitingRoomData.challengedReady;
    const opponentReady = waitingRoomData.isChallenger 
      ? waitingRoomData.challengedReady 
      : waitingRoomData.challengerReady;

    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: colors.background }]}
        edges={["top", "left", "right"]}
      >
        <View style={styles.waitingContainer}>
          {/* Header */}
          <Text style={[styles.waitingTitle, { color: colors.text }]}>
            🎮 Dammen Uitdaging
          </Text>

          {/* Character preview */}
          <View style={styles.waitingCharacters}>
            <View style={styles.waitingPlayer}>
              <Image
                source={CHARACTER_IMAGES.opa}
                style={styles.waitingAvatar}
                contentFit="contain"
              />
              <Text style={[styles.waitingPlayerName, { color: colors.text }]}>
                {waitingRoomData.challengerName}
              </Text>
              <View style={[
                styles.readyBadge,
                { backgroundColor: waitingRoomData.challengerReady ? colors.success : colors.border }
              ]}>
                <Text style={styles.readyBadgeText}>
                  {waitingRoomData.challengerReady ? "Klaar ✓" : "Wacht..."}
                </Text>
              </View>
            </View>

            <Text style={[styles.vsText, { color: colors.textSecondary }]}>VS</Text>

            <View style={styles.waitingPlayer}>
              <Image
                source={CHARACTER_IMAGES.oma}
                style={styles.waitingAvatar}
                contentFit="contain"
              />
              <Text style={[styles.waitingPlayerName, { color: colors.text }]}>
                {waitingRoomData.challengedName}
              </Text>
              <View style={[
                styles.readyBadge,
                { backgroundColor: waitingRoomData.challengedReady ? colors.success : colors.border }
              ]}>
                <Text style={styles.readyBadgeText}>
                  {waitingRoomData.challengedReady ? "Klaar ✓" : "Wacht..."}
                </Text>
              </View>
            </View>
          </View>

          {/* Status message */}
          <View style={[styles.statusCard, { backgroundColor: colors.surface }]}>
            {!myReady ? (
              <>
                <ActivityIndicator color={colors.primary} size="small" />
                <Text style={[styles.statusText, { color: colors.textSecondary }]}>
                  Klik op "Klaar" om te beginnen
                </Text>
              </>
            ) : !opponentReady ? (
              <>
                <ActivityIndicator color={colors.primary} size="small" />
                <Text style={[styles.statusText, { color: colors.textSecondary }]}>
                  Wachten op {waitingRoomData.isChallenger ? waitingRoomData.challengedName : waitingRoomData.challengerName}...
                </Text>
              </>
            ) : (
              <Text style={[styles.statusText, { color: colors.success }]}>
                Beide spelers zijn klaar! Het spel begint...
              </Text>
            )}
          </View>

          {/* Action buttons */}
          <View style={styles.waitingButtons}>
            {!myReady && (
              <TouchableOpacity
                style={[styles.readyButton, { backgroundColor: colors.success }]}
                onPress={handleReady}
              >
                <Ionicons name="checkmark-circle" size={24} color="#FFFFFF" />
                <Text style={styles.readyButtonText}>Klaar!</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.cancelButton, { borderColor: colors.error }]}
              onPress={handleCancelChallenge}
            >
              <Ionicons name="close-circle" size={20} color={colors.error} />
              <Text style={[styles.cancelButtonText, { color: colors.error }]}>
                Annuleren
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: colors.background }]}
      edges={["top", "left", "right"]}
    >
      {/* Header */}
      <DammenHeader
        currentTurn={currentTurn}
        myPiece={myPiece}
        isMyTurn={isMyTurn}
        opponentInfo={opponentInfo}
        myName={activeProfile?.nickname || activeProfile?.full_name || "Jij"}
        capturedPieces={capturedPieces}
        isAIGame={isAIGame}
        aiDifficulty={aiDifficulty}
      />

      {/* Game Board */}
      <View style={styles.boardContainer}>
        <DammenBoard
          board={board}
          selectedPosition={selectedPosition}
          validMoves={validMoves}
          mustJumpFrom={mustJumpFrom}
          myPiece={myPiece}
          isMyTurn={isMyTurn}
          onCellPress={selectCell}
          disabled={!isMyTurn || showResult}
        />
      </View>

      {/* Move Count */}
      <View style={[styles.moveCounter, { backgroundColor: colors.surface }]}>
        <Ionicons name="footsteps" size={16} color={colors.textSecondary} />
        <Text style={{ marginLeft: 8, fontSize: 12, color: colors.textSecondary }}>
          Zetten: {moveCount}
        </Text>
      </View>

      {/* Result Overlay */}
      {showResult && result && (
        <DammenResultOverlay
          result={result}
          myPiece={myPiece}
          myName={activeProfile?.nickname || activeProfile?.full_name || "Jij"}
          opponentName={opponentInfo?.name || "Computer"}
          pointsEarned={pointsEarned}
          isWeekendMode={mode === "weekend"}
          lottieKey={lottieKey}
          onPlayAgain={handlePlayAgain}
          onBack={handleBack}
        />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  boardContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  moveCounter: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
    marginHorizontal: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  // Waiting room styles
  waitingContainer: {
    flex: 1,
    padding: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  waitingTitle: {
    fontSize: 28,
    fontWeight: "700",
    marginBottom: 32,
  },
  waitingCharacters: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 24,
    marginBottom: 32,
  },
  waitingPlayer: {
    alignItems: "center",
    gap: 8,
  },
  waitingAvatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
  },
  waitingPlayerName: {
    fontSize: 16,
    fontWeight: "600",
  },
  readyBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  readyBadgeText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "600",
  },
  vsText: {
    fontSize: 24,
    fontWeight: "700",
  },
  statusCard: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderRadius: 12,
    marginBottom: 32,
  },
  statusText: {
    fontSize: 14,
  },
  waitingButtons: {
    gap: 16,
    width: "100%",
    maxWidth: 300,
  },
  readyButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 16,
    borderRadius: 12,
  },
  readyButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "700",
  },
  cancelButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: "600",
  },
});

